import type { JSX } from "react";

import { SkeletonPlaceholder } from "@expert/common-ui";
import { Box, Tooltip } from "@mantine/core";
import { useRef } from "react";

import type { ChatTask } from "@/sdk";

import { ChatPreviewBadge } from "./ChatPreviewBadge";

const MAX_WIDTH = 320;

/**
 * Displays the problem statement of a chat task as a badge.
 * It shows a popover with the full problem statement on hover if the text is truncated.
 */
export const ProblemStatement = ({ problemStatement }: Pick<ChatTask, "problemStatement">): JSX.Element => {
    const badgeRef = useRef<HTMLDivElement>(null);
    const rawValue = problemStatement.value;
    const isValidString = typeof rawValue === "string" && rawValue.length > 0;
    const hasValidStatement = !problemStatement.error && isValidString;
    const displayText = hasValidStatement ? rawValue : "Issue unknown";

    const isTruncated = !!(
        badgeRef.current?.children[0]?.scrollWidth &&
        badgeRef.current?.children[0]?.clientWidth &&
        badgeRef.current?.children[0]?.scrollWidth > badgeRef.current?.children[0]?.clientWidth
    );

    // Only show tooltip on hover when there's a valid statement that's truncated
    const showTooltipOnHover = hasValidStatement && isTruncated;

    return (
        <SkeletonPlaceholder visible={problemStatement.loading}>
            <Tooltip
                label={rawValue}
                multiline
                maw={MAX_WIDTH}
                position="bottom"
                events={{ hover: showTooltipOnHover, focus: false, touch: false }}
                transitionProps={{ duration: 300 }}
            >
                <Box fz={0} display="inline-flex" style={{ lineHeight: 0, alignItems: "center" }}>
                    {/* without a wrapper component the badge loses styles, fz={0} is also needed, the wrapper has extra height without it */}
                    <ChatPreviewBadge ref={badgeRef} color="primary.2" c="dark.9" maw={MAX_WIDTH}>
                        {displayText}
                    </ChatPreviewBadge>
                </Box>
            </Tooltip>
        </SkeletonPlaceholder>
    );
};
